/**
 * The public volunteer-teacher form (owner request 2026-10-20): an invitation to teach the Quran remotely
 * through Mubin, the conditions, and the application. No account; the administration reviews applications
 * (طلبات التطوع), interviews, and creates the teacher's account on acceptance.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award, CheckCircle2, Clock, HeartHandshake, MonitorSmartphone, Sparkles } from "lucide-react";
import type {
  Gender,
  MemorizedResponse,
  PublicVolunteerResponse,
  VolunteerExperience,
  VolunteerTime,
} from "@mubin/contracts";
import { CV_MAX_BYTES, CV_TYPES, VOLUNTEER_CONDITIONS, type VolunteerCvFile } from "@mubin/contracts";
import { ErrorNote, Field, useSubmit } from "../components/ui";
import { api, ApiError, errorText } from "../lib/api";
import { personGenderLabel } from "../lib/labels";
import { NATIONALITIES, experienceLabel, timeLabel } from "../lib/volunteers";
import { PhoneField } from "../components/phone-field";

const CONDITIONS = VOLUNTEER_CONDITIONS;

/** Reads a chosen CV file into the form's base64 payload. */
function readCv(file: File): Promise<VolunteerCvFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve({ name: file.name, type: file.type, data: url.slice(url.indexOf(",") + 1) });
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

const BENEFITS: { icon: ReactNode; title: string; text: string }[] = [
  {
    icon: <Award size={26} strokeWidth={1.6} />,
    title: "شهادة تطوع",
    text: "تمنحك الجمعية شهادة تطوع موثقة بساعات تعليمك.",
  },
  {
    icon: <MonitorSmartphone size={26} strokeWidth={1.6} />,
    title: "من بيتك عن بُعد",
    text: "تسمع لطلابك وتتابعهم من جوالك عبر تطبيق مُبين.",
  },
  {
    icon: <Clock size={26} strokeWidth={1.6} />,
    title: "في وقتك المناسب",
    text: "بعد الفجر أو العصر أو العشاء، أو في وقت تختاره.",
  },
  {
    icon: <HeartHandshake size={26} strokeWidth={1.6} />,
    title: "أجر لا ينقطع",
    text: "كل آية يتلوها طالبك بعدك في ميزانك بإذن الله.",
  },
];

export function VolunteerPage() {
  const info = useQuery({
    queryKey: ["public-volunteer"],
    queryFn: () => api<PublicVolunteerResponse>("/v1/public/volunteer"),
  });
  const { busy, error, run } = useSubmit();
  const [sent, setSent] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    nationalId: "",
    nationality: "",
    gender: "" as Gender | "",
    phone: "",
    email: "",
    city: "",
    fromSurah: "",
    toSurah: "",
    hasIjazah: "" as "" | "yes" | "no",
    times: [] as VolunteerTime[],
    timesOther: "",
    experience: "" as VolunteerExperience | "",
    note: "",
    cvUrl: "",
    agree: false,
    website: "",
  });
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  const juz = useQuery({
    queryKey: ["memorized", form.fromSurah, form.toSurah],
    queryFn: () => api<MemorizedResponse>(`/v1/public/memorized?from=${form.fromSurah}&to=${form.toSurah}`),
    enabled: !!form.fromSurah && !!form.toSurah,
  });
  const org = info.data;
  const ready =
    !!org &&
    form.name.trim().split(/\s+/).length >= 4 &&
    /^[A-Za-z0-9]{5,20}$/.test(form.nationalId.trim()) &&
    (!form.cvUrl.trim() || /^https?:\/\/\S+\.\S+/.test(form.cvUrl.trim())) &&
    !cvError &&
    !!form.nationality &&
    !!form.gender &&
    form.phone.trim().length >= 9 &&
    /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
    !!form.fromSurah &&
    !!form.toSurah &&
    !!form.hasIjazah &&
    form.times.length > 0 &&
    (!form.times.includes("other") || form.timesOther.trim().length > 0) &&
    form.agree;

  function toggleTime(t: VolunteerTime, on: boolean) {
    set({ times: on ? [...form.times, t] : form.times.filter((x) => x !== t) });
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    void run(
      async () => {
        await api("/v1/public/volunteer-applications", {
          method: "POST",
          json: {
            organizationId: org.organizationId,
            name: form.name.trim().replace(/\s+/g, " "),
            nationalId: form.nationalId,
            nationality: form.nationality,
            gender: form.gender,
            phone: form.phone.trim(),
            email: form.email.trim(),
            city: form.city.trim() || undefined,
            fromSurah: Number(form.fromSurah),
            toSurah: Number(form.toSurah),
            hasIjazah: form.hasIjazah === "yes",
            times: form.times,
            timesOther: form.times.includes("other") ? form.timesOther.trim() : undefined,
            experience: form.experience || undefined,
            note: form.note.trim() || undefined,
            cvUrl: form.cvUrl.trim() || undefined,
            cv: cvFile ? await readCv(cvFile) : undefined,
            agree: form.agree,
            website: form.website || undefined,
          },
        });
        setSent(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
      (err) =>
        err instanceof ApiError && err.status === 429
          ? "أُرسلت طلبات كثيرة من هذا الجهاز. حاول لاحقًا."
          : errorText(err),
    );
  }

  return (
    <main className="vol">
      <header className="vol-hero">
        <div className="vol-logos">
          <img src="/org/horizontal-white.png" alt={org?.organizationName ?? "الجمعية"} className="vol-org" />
          <span className="vol-sep" aria-hidden="true" />
          <img src="/logo-ivory.png" alt="مُبين" className="vol-mubin" />
        </div>
        <p className="vol-kicker">
          <Sparkles size={16} strokeWidth={1.75} /> فرصة تطوع
        </p>
        <h1>كن معلّمًا لكتاب الله</h1>
        <p className="vol-lead">
          يسرّ {org?.organizationName ?? "الجمعية"} أن تدعوك للانضمام إلى فريق معلميها المتطوعين، لتعليم
          القرآن الكريم عن بُعد عبر تطبيق <b>مُبين</b>.
        </p>
        <blockquote className="vol-hadith">«خيرُكم مَن تعلَّم القرآنَ وعلَّمه»</blockquote>
        <div className="vol-ctas">
          {!sent && (
            <a className="btn vol-cta" href="#vol-form">
              سجّل الآن
            </a>
          )}
          <a className="btn vol-cta vol-cta-app" href="/download">
            حمّل التطبيق
          </a>
        </div>
      </header>

      <div className="vol-body">
        {sent ? (
          <section className="vol-card vol-done" aria-live="polite">
            <CheckCircle2 size={54} strokeWidth={1.5} />
            <h2>جزاك الله خيرًا، وصل طلبك</h2>
            <p>سنراجع طلبك ونتواصل معك عبر الواتساب لتحديد موعد المقابلة الشخصية.</p>
            <ol className="vol-steps">
              <li>مراجعة الطلب</li>
              <li>المقابلة الشخصية</li>
              <li>إنشاء حسابك في تطبيق مُبين وبدء التعليم</li>
            </ol>
            <p className="muted small">
              لا حاجة لإعادة الإرسال؛ الطلب المكرر برقم الهوية نفسه لا يُسجَّل مرتين.
            </p>
          </section>
        ) : (
          <>
            <section className="vol-benefits">
              {BENEFITS.map((b) => (
                <div key={b.title} className="vol-benefit">
                  <span className="vol-icon">{b.icon}</span>
                  <b>{b.title}</b>
                  <span>{b.text}</span>
                </div>
              ))}
            </section>

            <section className="vol-card vol-app">
              <div>
                <h2>التعليم عبر تطبيق مُبين</h2>
                <p>
                  تستمع لتلاوة طلابك صوتيًا مباشرة من التطبيق، وتسجّل الأخطاء والتقييم في موضعها من صفحة
                  المصحف، ويصلهم المقرر والتنبيهات تلقائيًا.
                </p>
              </div>
              {/* iPhone goes straight to the App Store; Android to the download page with the file and install steps. */}
              <div className="vol-stores">
                <a
                  className="vol-store"
                  href="https://apps.apple.com/app/id6821867000"
                  target="_blank"
                  rel="noreferrer"
                >
                  <AppleIcon />
                  <span>
                    <small>حمّل للآيفون</small>iPhone
                  </span>
                </a>
                <a className="vol-store" href="/download">
                  <PlayIcon />
                  <span>
                    <small>حمّل للأندرويد</small>Android
                  </span>
                </a>
              </div>
            </section>

            <section className="vol-card">
              <h2>شروط التطوع</h2>
              <ul className="vol-conditions">
                {CONDITIONS.map((c) => (
                  <li key={c}>
                    <CheckCircle2 size={18} strokeWidth={1.75} />
                    {c}
                  </li>
                ))}
              </ul>
            </section>

            <form id="vol-form" className="vol-card form" onSubmit={submit} noValidate>
              <h2>نموذج التسجيل</h2>
              <ErrorNote error={error ?? (info.isError ? "تعذّر تحميل النموذج. حدّث الصفحة." : null)} />

              <h3 className="vol-h3">بياناتك</h3>
              <Field id="v-name" label="الاسم الرباعي">
                <input
                  id="v-name"
                  autoComplete="name"
                  placeholder="الاسم الأول واسم الأب والجد والعائلة"
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  required
                />
              </Field>
              <div className="form-row">
                <Field id="v-id" label="رقم الهوية أو الإقامة أو الجواز">
                  <input
                    id="v-id"
                    dir="ltr"
                    maxLength={20}
                    value={form.nationalId}
                    onChange={(e) =>
                      set({
                        nationalId: e.target.value
                          .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
                          .replace(/[^0-9A-Za-z]/g, "")
                          .toUpperCase(),
                      })
                    }
                    required
                  />
                </Field>
                <Field id="v-nat" label="الجنسية">
                  <select
                    id="v-nat"
                    value={form.nationality}
                    onChange={(e) => set({ nationality: e.target.value })}
                    required
                  >
                    <option value="">اختر الجنسية</option>
                    {NATIONALITIES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="form-row">
                <Field id="v-gender" label="الجنس" hint="الحلقات منفصلة للبنين والبنات">
                  <select
                    id="v-gender"
                    value={form.gender}
                    onChange={(e) => set({ gender: e.target.value as Gender | "" })}
                    required
                  >
                    <option value="">اختر</option>
                    <option value="male">{personGenderLabel.male}</option>
                    <option value="female">{personGenderLabel.female}</option>
                  </select>
                </Field>
                <Field id="v-phone" label="رقم الواتساب">
                  <PhoneField id="v-phone" value={form.phone} onChange={(phone) => set({ phone })} required />
                </Field>
              </div>
              <div className="form-row">
                <Field id="v-email" label="البريد الإلكتروني">
                  <input
                    id="v-email"
                    type="email"
                    dir="ltr"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => set({ email: e.target.value })}
                    required
                  />
                </Field>
                <Field id="v-city" label="المدينة (اختياري)">
                  <input id="v-city" value={form.city} onChange={(e) => set({ city: e.target.value })} />
                </Field>
              </div>

              <h3 className="vol-h3">حفظك وإجازتك</h3>
              <div className="form-row">
                <Field id="v-from" label="تحفظ من سورة">
                  <SurahSelect
                    id="v-from"
                    value={form.fromSurah}
                    onChange={(v) => set({ fromSurah: v })}
                    surahs={org?.surahs}
                  />
                </Field>
                <Field id="v-to" label="إلى سورة">
                  <SurahSelect
                    id="v-to"
                    value={form.toSurah}
                    onChange={(v) => set({ toSurah: v })}
                    surahs={org?.surahs}
                  />
                </Field>
              </div>
              {juz.data && (
                <p className="vol-juz" aria-live="polite">
                  مقدار حفظك: <b>{juz.data.juz.toLocaleString("ar-SA")} جزء</b>{" "}
                  <span className="muted small">
                    ({juz.data.pages.toLocaleString("ar-SA")} صفحة من مصحف المدينة)
                  </span>
                </p>
              )}
              <div className="form-row">
                <Field id="v-ijazah" label="هل لديك إجازة في القرآن الكريم؟">
                  <div className="vol-choices" role="radiogroup">
                    {(["yes", "no"] as const).map((v) => (
                      <label key={v} className={`vol-choice${form.hasIjazah === v ? " on" : ""}`}>
                        <input
                          type="radio"
                          name="ijazah"
                          checked={form.hasIjazah === v}
                          onChange={() => set({ hasIjazah: v })}
                        />
                        {v === "yes" ? "نعم" : "لا"}
                      </label>
                    ))}
                  </div>
                </Field>
                <Field id="v-exp" label="خبرتك في التحفيظ (اختياري)">
                  <select
                    id="v-exp"
                    value={form.experience}
                    onChange={(e) => set({ experience: e.target.value as VolunteerExperience | "" })}
                  >
                    <option value="">اختر</option>
                    {(Object.keys(experienceLabel) as VolunteerExperience[]).map((k) => (
                      <option key={k} value={k}>
                        {experienceLabel[k]}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <h3 className="vol-h3">الأوقات المناسبة لك</h3>
              <div className="vol-choices">
                {(Object.keys(timeLabel) as VolunteerTime[]).map((t) => (
                  <label key={t} className={`vol-choice${form.times.includes(t) ? " on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={form.times.includes(t)}
                      onChange={(e) => toggleTime(t, e.target.checked)}
                    />
                    {timeLabel[t]}
                  </label>
                ))}
              </div>
              {form.times.includes("other") && (
                <Field id="v-other" label="اذكر الوقت المناسب">
                  <input
                    id="v-other"
                    value={form.timesOther}
                    onChange={(e) => set({ timesOther: e.target.value })}
                  />
                </Field>
              )}
              <Field
                id="v-cv"
                label="السيرة الذاتية (اختياري)"
                hint={`ملف PDF أو Word أو صورة حتى ${CV_MAX_BYTES / 1024 / 1024} ميجابايت، أو رابط لها`}
              >
                <input
                  id="v-cv"
                  type="file"
                  accept={Object.keys(CV_TYPES).join(",")}
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setCvFile(file);
                    setCvError(
                      !file
                        ? null
                        : !Object.hasOwn(CV_TYPES, file.type)
                          ? "السيرة الذاتية: PDF أو Word أو صورة"
                          : file.size > CV_MAX_BYTES
                            ? "حجم الملف أكبر من 5 ميجابايت"
                            : null,
                    );
                  }}
                />
              </Field>
              {cvError && <p className="alert">{cvError}</p>}
              <Field id="v-cv-url" label="أو رابط السيرة الذاتية (اختياري)">
                <input
                  id="v-cv-url"
                  type="url"
                  dir="ltr"
                  placeholder="https://"
                  maxLength={500}
                  value={form.cvUrl}
                  onChange={(e) => set({ cvUrl: e.target.value })}
                />
              </Field>
              <Field id="v-note" label="ما تودّ إضافته (اختياري)">
                <textarea
                  id="v-note"
                  maxLength={500}
                  value={form.note}
                  onChange={(e) => set({ note: e.target.value })}
                />
              </Field>

              <div className="honeypot" aria-hidden="true">
                <label htmlFor="v-website">الموقع</label>
                <input
                  id="v-website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => set({ website: e.target.value })}
                />
              </div>
              <label className="vol-agree">
                <input
                  type="checkbox"
                  checked={form.agree}
                  onChange={(e) => set({ agree: e.target.checked })}
                />
                أقرّ بأن البيانات صحيحة، وأوافق على شروط التطوع.
              </label>
              <p className="muted small">
                نستخدم بياناتك لمراجعة طلبك والتواصل معك فقط، ولا يُنشأ حساب قبل قبولك. اقرأ{" "}
                <a href="/privacy">سياسة الخصوصية</a>.
              </p>
              <div className="form-actions">
                <button className="btn btn-primary vol-submit" disabled={busy || !ready}>
                  {busy ? "جارٍ الإرسال…" : "أرسل طلب التطوع"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
      <footer className="vol-foot">
        <img src="/org/horizontal-color.png" alt={org?.organizationName ?? "الجمعية"} />
      </footer>
    </main>
  );
}

function SurahSelect({
  id,
  value,
  onChange,
  surahs,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  surahs?: PublicVolunteerResponse["surahs"];
}) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} required>
      <option value="">{surahs ? "اختر السورة" : "جارٍ التحميل…"}</option>
      {surahs?.map((s) => (
        <option key={s.id} value={s.id}>
          {s.id.toLocaleString("ar-SA")}. {s.name}
        </option>
      ))}
    </select>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 384 512" width="22" height="22" aria-hidden="true" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 512 512" width="22" height="22" aria-hidden="true">
      <path
        fill="#34A853"
        d="M48 59.5v393c0 6 3.4 11.3 8.4 13.9L268 256 56.4 45.6c-5 2.6-8.4 7.9-8.4 13.9z"
      />
      <path fill="#FBBC04" d="M346 334.4 268 256l78-78.4 92.5 53.2c16.6 9.6 16.6 33.6 0 43.2z" />
      <path fill="#EA4335" d="M268 256 56.4 466.4c4.6 2.4 10.2 2.6 15.2-.3L346 334.4z" />
      <path fill="#4285F4" d="M346 177.6 71.6 45.9c-5-2.9-10.6-2.7-15.2-.3L268 256z" />
    </svg>
  );
}
